import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Container, Typography, Tabs, Tab, Card, CardContent,
    Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Chip, IconButton, TextField, InputAdornment,
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    Select, MenuItem, FormControl, InputLabel, Alert, CircularProgress,
    Tooltip, LinearProgress, ToggleButton, ToggleButtonGroup
} from '@mui/material';
import {
    People, Description, BarChart, Inventory,
    Search, Edit, Block, CheckCircle, Refresh,
    AdminPanelSettings, BugReport
} from '@mui/icons-material';
import { FddApi } from '../../ui/services/server-fdd';
import SocketApi from '../../ui/services/socket-fdd';

// ── Helpers ──────────────────────────────────────────────
const statusColor = (status) => ({
    active: 'success', suspended: 'warning', disabled: 'error'
}[status] || 'default');

const statusLabel = (status) => ({
    active: 'Activo', suspended: 'Suspendido', disabled: 'Desactivado'
}[status] || status || '—');

const StatCard = ({ icon, label, value, color = 'primary.main' }) => (
    <Card>
        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ color, fontSize: 40, display: 'flex' }}>{icon}</Box>
            <Box>
                <Typography variant="body2" color="text.secondary">{label}</Typography>
                <Typography variant="h4" fontWeight="700">{value ?? '—'}</Typography>
            </Box>
        </CardContent>
    </Card>
);

// ─────────────────────────────────────────────────────────
// TABS
// ─────────────────────────────────────────────────────────

/** Tab 0: Estadísticas globales */
function StatsTab({ stats, loading }) {
    if (loading) return <Box sx={{ mt: 4, textAlign: 'center' }}><CircularProgress /></Box>;
    if (!stats) return <Alert severity="error" sx={{ mt: 3 }}>No se pudieron cargar las estadísticas</Alert>;

    const { users, documents, signatures, storage, planDistribution } = stats;

    return (
        <Box sx={{ mt: 3 }}>
            <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard icon={<People fontSize="inherit" />} label="Total usuarios" value={users.total_users} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard icon={<CheckCircle fontSize="inherit" />} label="Usuarios activos" value={users.active_users} color="success.main" />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard icon={<Description fontSize="inherit" />} label="Documentos totales" value={documents.total_drafts} color="info.main" />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard icon={<Description fontSize="inherit" />} label="Firmas completadas" value={signatures.completed_signatures} color="secondary.main" />
                </Grid>
            </Grid>

            <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>Distribución por plan</Typography>
            {planDistribution?.map(p => (
                <Box key={p.name} sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2">{p.name}</Typography>
                        <Typography variant="body2" fontWeight={600}>{p.subscribers} suscriptores</Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={Math.min(100, (p.subscribers / (users.total_users || 1)) * 100)}
                        sx={{ height: 8, borderRadius: 4 }}
                    />
                </Box>
            ))}

            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Almacenamiento total usado: <strong>{(storage.total_storage_mb / 1024).toFixed(2)} GB</strong>
            </Typography>
        </Box>
    );
}

/** Tab 1: Usuarios */
function UsersTab({ plans }) {
    const [users, setUsers] = useState([]);
    const [total, setTotal] = useState(0);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);

    // Modal cambiar plan
    const [planDialog, setPlanDialog] = useState({ open: false, user: null, selectedPlan: '' });
    // Modal cambiar estado
    const [statusDialog, setStatusDialog] = useState({ open: false, user: null, selectedStatus: '' });
    const [actionLoading, setActionLoading] = useState(false);
    const [feedback, setFeedback] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await FddApi.adminGetUsers(search);
            if (!r.hasError) { setUsers(r.users); setTotal(r.total); }
        } finally {
            setLoading(false);
        }
    }, [search]);

    useEffect(() => { const t = setTimeout(load, 350); return () => clearTimeout(t); }, [load]);

    const handleChangePlan = async () => {
        setActionLoading(true);
        const r = await FddApi.adminUpdateUserPlan(planDialog.user.id, planDialog.selectedPlan);
        setActionLoading(false);
        setPlanDialog({ open: false, user: null, selectedPlan: '' });
        setFeedback({ type: r.hasError ? 'error' : 'success', msg: r.hasError ? r.message : r.message });
        if (!r.hasError) load();
    };

    const handleChangeStatus = async () => {
        setActionLoading(true);
        const r = await FddApi.adminUpdateUserStatus(statusDialog.user.id, statusDialog.selectedStatus);
        setActionLoading(false);
        setStatusDialog({ open: false, user: null, selectedStatus: '' });
        setFeedback({ type: r.hasError ? 'error' : 'success', msg: r.message });
        if (!r.hasError) load();
    };

    return (
        <Box sx={{ mt: 3 }}>
            {feedback && <Alert severity={feedback.type} sx={{ mb: 2 }} onClose={() => setFeedback(null)}>{feedback.msg}</Alert>}

            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                    size="small" placeholder="Buscar por nombre o email"
                    value={search} onChange={e => setSearch(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
                    sx={{ flex: 1 }}
                />
                <Tooltip title="Recargar"><IconButton onClick={load}><Refresh /></IconButton></Tooltip>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{total} usuarios encontrados</Typography>

            {loading ? <LinearProgress /> : (
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>ID</TableCell>
                                <TableCell>Nombre</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Plan</TableCell>
                                <TableCell>Estado</TableCell>
                                <TableCell>Verificado</TableCell>
                                <TableCell>Admin</TableCell>
                                <TableCell align="center">Acciones</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users.map(u => (
                                <TableRow key={u.id} hover>
                                    <TableCell>{u.id}</TableCell>
                                    <TableCell>{u.first_name} {u.last_name}</TableCell>
                                    <TableCell>{u.email}</TableCell>
                                    <TableCell>
                                        <Chip size="small" label={u.plan_name || 'Sin plan'} />
                                    </TableCell>
                                    <TableCell>
                                        <Chip size="small" color={statusColor(u.account_status)} label={statusLabel(u.account_status)} />
                                    </TableCell>
                                    <TableCell>
                                        {u.email_verified ? <CheckCircle color="success" fontSize="small" /> : <Block color="disabled" fontSize="small" />}
                                    </TableCell>
                                    <TableCell>
                                        {u.is_admin ? <Chip size="small" label="Admin" color="warning" /> : '—'}
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="Cambiar plan">
                                            <IconButton size="small" onClick={() => setPlanDialog({ open: true, user: u, selectedPlan: u.plan_id || '' })}>
                                                <Edit fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Cambiar estado">
                                            <IconButton size="small" onClick={() => setStatusDialog({ open: true, user: u, selectedStatus: u.account_status || 'active' })}>
                                                <Block fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Dialog: cambiar plan */}
            <Dialog open={planDialog.open} onClose={() => setPlanDialog({ open: false, user: null, selectedPlan: '' })} maxWidth="xs" fullWidth>
                <DialogTitle>Cambiar plan — {planDialog.user?.email}</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel>Plan</InputLabel>
                        <Select value={planDialog.selectedPlan} label="Plan" onChange={e => setPlanDialog(d => ({ ...d, selectedPlan: e.target.value }))}>
                            {plans.map(p => <MenuItem key={p.pk_id_plan} value={p.pk_id_plan}>{p.name} (${p.price_monthly}/mes)</MenuItem>)}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPlanDialog({ open: false, user: null, selectedPlan: '' })}>Cancelar</Button>
                    <Button variant="contained" onClick={handleChangePlan} disabled={actionLoading}>
                        {actionLoading ? <CircularProgress size={18} /> : 'Guardar'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog: cambiar estado */}
            <Dialog open={statusDialog.open} onClose={() => setStatusDialog({ open: false, user: null, selectedStatus: '' })} maxWidth="xs" fullWidth>
                <DialogTitle>Cambiar estado — {statusDialog.user?.email}</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel>Estado</InputLabel>
                        <Select value={statusDialog.selectedStatus} label="Estado" onChange={e => setStatusDialog(d => ({ ...d, selectedStatus: e.target.value }))}>
                            <MenuItem value="active">Activo</MenuItem>
                            <MenuItem value="suspended">Suspendido</MenuItem>
                            <MenuItem value="disabled">Desactivado</MenuItem>
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setStatusDialog({ open: false, user: null, selectedStatus: '' })}>Cancelar</Button>
                    <Button variant="contained" color="warning" onClick={handleChangeStatus} disabled={actionLoading}>
                        {actionLoading ? <CircularProgress size={18} /> : 'Guardar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

/** Tab 2: Documentos */
function DocumentsTab() {
    const [docs, setDocs] = useState([]);
    const [total, setTotal] = useState(0);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await FddApi.adminGetDocuments(search);
            if (!r.hasError) { setDocs(r.documents); setTotal(r.total); }
        } finally { setLoading(false); }
    }, [search]);

    useEffect(() => { const t = setTimeout(load, 350); return () => clearTimeout(t); }, [load]);

    const docStatusColor = (s) => ({ Borrador: 'default', Enviado: 'info', Completado: 'success', Cancelado: 'error' }[s] || 'default');

    return (
        <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                    size="small" placeholder="Buscar por título o email"
                    value={search} onChange={e => setSearch(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
                    sx={{ flex: 1 }}
                />
                <Tooltip title="Recargar"><IconButton onClick={load}><Refresh /></IconButton></Tooltip>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{total} documentos encontrados</Typography>

            {loading ? <LinearProgress /> : (
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Título</TableCell>
                                <TableCell>Propietario</TableCell>
                                <TableCell>Estado</TableCell>
                                <TableCell>Firmantes</TableCell>
                                <TableCell>Creado</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {docs.map(d => (
                                <TableRow key={d.id} hover>
                                    <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {d.title || '(sin título)'}
                                    </TableCell>
                                    <TableCell>{d.owner_email || '—'}</TableCell>
                                    <TableCell>
                                        <Chip size="small" color={docStatusColor(d.status)} label={d.status || '—'} />
                                    </TableCell>
                                    <TableCell>{d.completed_signers}/{d.total_signers}</TableCell>
                                    <TableCell>{d.date_created ? new Date(d.date_created).toLocaleDateString('es-GT') : '—'}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
}

/** Tab 3: Planes */
function PlansTab({ plans, onReload }) {
    const [editDialog, setEditDialog] = useState({ open: false, plan: null });
    const [formData, setFormData] = useState({});
    const [loading, setLoading] = useState(false);
    const [feedback, setFeedback] = useState(null);

    const openEdit = (plan) => {
        setFormData({
            priceMonthly: plan.price_monthly,
            priceYearly: plan.price_yearly,
            documentsLimit: plan.documents_limit ?? '',
            signersLimit: plan.signers_limit ?? '',
            storageLimitMb: plan.storage_limit_mb ?? ''
        });
        setEditDialog({ open: true, plan });
    };

    const handleSave = async () => {
        setLoading(true);
        const r = await FddApi.adminUpdatePlan(editDialog.plan.pk_id_plan, formData);
        setLoading(false);
        setEditDialog({ open: false, plan: null });
        setFeedback({ type: r.hasError ? 'error' : 'success', msg: r.message });
        if (!r.hasError) onReload();
    };

    const field = (key, label, type = 'number') => (
        <TextField
            key={key} fullWidth margin="dense" size="small" type={type} label={label}
            value={formData[key] ?? ''}
            onChange={e => setFormData(d => ({ ...d, [key]: e.target.value === '' ? null : e.target.value }))}
            helperText={formData[key] === null || formData[key] === '' ? 'Vacío = ilimitado' : ''}
        />
    );

    return (
        <Box sx={{ mt: 3 }}>
            {feedback && <Alert severity={feedback.type} sx={{ mb: 2 }} onClose={() => setFeedback(null)}>{feedback.msg}</Alert>}

            <Grid container spacing={3}>
                {plans.map(p => (
                    <Grid item xs={12} sm={6} md={4} key={p.pk_id_plan}>
                        <Card>
                            <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="h6" fontWeight="700">{p.name}</Typography>
                                    <Tooltip title="Editar plan">
                                        <IconButton size="small" onClick={() => openEdit(p)}><Edit fontSize="small" /></IconButton>
                                    </Tooltip>
                                </Box>
                                <Typography variant="h5" color="primary.main" sx={{ mt: 1 }}>${p.price_monthly}<Typography component="span" variant="body2">/mes</Typography></Typography>
                                <Box component="ul" sx={{ pl: 2, mt: 1, mb: 0 }}>
                                    <li><Typography variant="body2">Docs: {p.documents_limit ?? '∞'}</Typography></li>
                                    <li><Typography variant="body2">Firmantes: {p.signers_limit ?? '∞'}</Typography></li>
                                    <li><Typography variant="body2">Storage: {p.storage_limit_mb ? `${p.storage_limit_mb} MB` : '∞'}</Typography></li>
                                    <li><Typography variant="body2">Suscriptores: <strong>{p.total_subscribers}</strong></Typography></li>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, plan: null })} maxWidth="xs" fullWidth>
                <DialogTitle>Editar plan: {editDialog.plan?.name}</DialogTitle>
                <DialogContent>
                    {field('priceMonthly', 'Precio mensual (USD)')}
                    {field('priceYearly', 'Precio anual (USD)')}
                    {field('documentsLimit', 'Límite de documentos')}
                    {field('signersLimit', 'Límite de firmantes')}
                    {field('storageLimitMb', 'Límite de almacenamiento (MB)')}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialog({ open: false, plan: null })}>Cancelar</Button>
                    <Button variant="contained" onClick={handleSave} disabled={loading}>
                        {loading ? <CircularProgress size={18} /> : 'Guardar cambios'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

// ─────────────────────────────────────────────────────────
// PÁGINA PRINCIPAL
// ─────────────────────────────────────────────────────────

const LOG_LEVEL_COLORS = {
    error: 'error', warn: 'warning', info: 'info',
    http: 'default', debug: 'default', unknown: 'default',
};

/** Tab 4: Logs del sistema */
function LogsTab() {
    const [logs, setLogs] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [file, setFile] = useState('combined');
    const [level, setLevel] = useState('');
    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [live, setLive] = useState(false);
    const socketRef = useRef(null);
    const MAX_LIVE_LOGS = 500;

    const load = useCallback(async () => {
        setLoading(true);
        const r = await FddApi.adminGetLogs({ file, level, search, limit: 300 });
        if (!r.hasError) { setLogs(r.logs); setTotal(r.total); }
        setLoading(false);
    }, [file, level, search]);

    useEffect(() => { load(); }, [load]);

    // Debounce del search
    useEffect(() => {
        const t = setTimeout(() => setSearch(searchInput), 400);
        return () => clearTimeout(t);
    }, [searchInput]);

    // Socket en tiempo real
    useEffect(() => {
        if (!live) {
            if (socketRef.current) {
                socketRef.current.leaveAdminLogs();
                socketRef.current.disconnect();
                socketRef.current = null;
            }
            return;
        }
        const s = new SocketApi();
        s.connect();
        s.joinAdminLogs(
            (entry) => {
                setLogs(prev => {
                    const updated = [entry, ...prev];
                    return updated.slice(0, MAX_LIVE_LOGS);
                });
                setTotal(t => t + 1);
            }
        );
        socketRef.current = s;
        return () => {
            s.leaveAdminLogs();
            s.disconnect();
            socketRef.current = null;
        };
    }, [live]);

    const rowBg = (lvl) => ({
        error: 'rgba(211,47,47,0.07)',
        warn: 'rgba(237,108,2,0.06)',
    }[lvl] || undefined);

    return (
        <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 130 }}>
                    <InputLabel>Archivo</InputLabel>
                    <Select value={file} label="Archivo" onChange={e => setFile(e.target.value)} disabled={live}>
                        <MenuItem value="combined">combined.log</MenuItem>
                        <MenuItem value="error">error.log</MenuItem>
                    </Select>
                </FormControl>

                <ToggleButtonGroup size="small" exclusive value={level} onChange={(_, v) => setLevel(v ?? '')} disabled={live}>
                    <ToggleButton value="">Todos</ToggleButton>
                    <ToggleButton value="error">Error</ToggleButton>
                    <ToggleButton value="warn">Warn</ToggleButton>
                    <ToggleButton value="info">Info</ToggleButton>
                    <ToggleButton value="http">HTTP</ToggleButton>
                </ToggleButtonGroup>

                <TextField
                    size="small" placeholder="Buscar en logs..."
                    value={searchInput} onChange={e => setSearchInput(e.target.value)}
                    disabled={live}
                    InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
                    sx={{ flex: 1, minWidth: 200 }}
                />

                <Tooltip title={live ? 'Desactivar tiempo real' : 'Activar tiempo real'}>
                    <Chip
                        label={live ? '⏺ En vivo' : '⏸ En vivo'}
                        color={live ? 'success' : 'default'}
                        onClick={() => setLive(v => !v)}
                        sx={{ cursor: 'pointer', fontWeight: 600 }}
                    />
                </Tooltip>

                <Tooltip title="Recargar"><IconButton onClick={load} disabled={live}><Refresh /></IconButton></Tooltip>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {total} entradas {live ? '— actualizando en tiempo real' : '(más recientes primero)'}
            </Typography>

            {loading ? <LinearProgress /> : (
                <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: 170 }}>Fecha</TableCell>
                                <TableCell sx={{ width: 80 }}>Nivel</TableCell>
                                <TableCell>Mensaje</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {logs.map(l => (
                                <TableRow key={l.id} sx={{ bgcolor: rowBg(l.level) }}>
                                    <TableCell sx={{ whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: 12 }}>
                                        {l.timestamp}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={l.level.toUpperCase()}
                                            color={LOG_LEVEL_COLORS[l.level] || 'default'}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>
                                        {l.message}
                                        {l.meta && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                {JSON.stringify(l.meta)}
                                            </Typography>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                            {logs.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={3} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                        Sin entradas de log
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
}

export const AdminPage = () => {
    const navigate = useNavigate();
    const [tab, setTab] = useState(0);
    const [stats, setStats] = useState(null);
    const [plans, setPlans] = useState([]);
    const [loadingStats, setLoadingStats] = useState(true);
    const [unauthorized, setUnauthorized] = useState(false);

    const loadStats = useCallback(async () => {
        setLoadingStats(true);
        const r = await FddApi.adminGetStats();
        if (r.statusCode === 403) { setUnauthorized(true); return; }
        if (!r.hasError) setStats(r.stats);
        setLoadingStats(false);
    }, []);

    const loadPlans = useCallback(async () => {
        const r = await FddApi.adminGetPlans();
        if (!r.hasError) setPlans(r.plans);
    }, []);

    useEffect(() => {
        loadStats();
        loadPlans();
    }, [loadStats, loadPlans]);

    if (unauthorized) {
        return (
            <Container maxWidth="sm" sx={{ mt: 8, textAlign: 'center' }}>
                <Alert severity="error" sx={{ mb: 3 }}>
                    No tienes permisos para acceder al Panel de Administración.
                </Alert>
                <Button variant="contained" onClick={() => navigate('/')}>Ir al inicio</Button>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                <AdminPanelSettings sx={{ fontSize: 36, color: 'primary.main' }} />
                <Box>
                    <Typography variant="h4" fontWeight="700">Panel de Administración</Typography>
                    <Typography variant="body2" color="text.secondary">Neftis Secure Sign — Vista de administrador</Typography>
                </Box>
            </Box>

            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={tab} onChange={(_, v) => setTab(v)}>
                    <Tab icon={<BarChart />} iconPosition="start" label="Estadísticas" />
                    <Tab icon={<People />} iconPosition="start" label="Usuarios" />
                    <Tab icon={<Description />} iconPosition="start" label="Documentos" />
                    <Tab icon={<Inventory />} iconPosition="start" label="Planes" />
                    <Tab icon={<BugReport />} iconPosition="start" label="Logs" />
                </Tabs>
            </Box>

            {tab === 0 && <StatsTab stats={stats} loading={loadingStats} />}
            {tab === 1 && <UsersTab plans={plans} />}
            {tab === 2 && <DocumentsTab />}
            {tab === 3 && <PlansTab plans={plans} onReload={loadPlans} />}
            {tab === 4 && <LogsTab />}
        </Container>
    );
};

export default AdminPage;
